import React, { useState, useEffect, useContext } from "react";
import "./App.css";
import Login from "./components/Login/Login";
import SignUp from "./components/SignUp/index";
import PhoneVerification from "./components/PhoneVerification/PhoneVerification";
import ForgotPassword from "./components/ForgotPassword/ForgotPassword";
import ResetPassword from "./components/ForgotPassword/ResetPasswordForm";
import CompanyProfile from "./components/CompanyProfile/CompanyProfile";
import Settings from "./components/Settings/index";
// import DataExport from "./components/DataExport/index"; // Disabled - export feature hidden from all users
import SubscriptionGate from "./components/SubscriptionPlans/SubscriptionGate";
import Dashboard from "./components/Dashboard/Dashboard";
import WeatherDetailsPage from "./components/Weather/WeatherDetailsPage";
import FieldsList from "./components/Fields/FieldsList";
import FieldDetails from "./components/Fields/FieldDetails";
import FieldForm from "./components/Fields/FieldForm";
import TransactionForm from "./components/Transactions/TransactionForm";
import FieldMapEditor from "./components/Fields/FieldMapEditor";
import CropCycleForm from "./components/Fields/CropCycleForm";
import Tasks from "./components/Tasks";
import TaskDetails from "./components/Tasks/TaskDetails";
import TaskForm from "./components/Tasks/TaskForm";
import TaskListSelector from "./components/Tasks/TaskListSelector";
import TaskListForm from "./components/Tasks/TaskListForm";
import ProblemsList from "./components/Problems/ProblemsList";
import ProblemDetails from "./components/Problems/ProblemDetails";
import ProblemForm from "./components/Problems/ProblemForm";
import Payments from "./components/Expenses & Sales/index";
import Invoices from "./components/Invoices/index";
import InvoiceDetails from "./components/Invoices/InvoiceDetails";
import EmployeesList from "./components/Employees/EmployeesList";
import SalariesSummary from "./components/Employees/SalariesSummary";
import EmployeeProfile from "./components/Employees/EmployeeProfile";
import AddTransactionPage from "./components/Employees/AddTransactionPage";
import ProductTransactions from "./components/Inventory/Warehouses/WarehouseProducts/ProductTransactions/index";
import AddProductMovementTransaction from "./components/Inventory/Warehouses/WarehouseProducts/ProductTransactions/AddProductMovementTransaction";
import EmployeeForm from "./components/Employees/EmployeeForm";
import FieldSelector from "./components/Employees/FieldSelector";
import SetRole from "./components/Employees/SetRole";
import WorkerSelectorPage from "./components/Employees/WorkerSelectorPage";
import Sidebar from "./components/Sidebar/Sidebar";
import ProductsApp from "./components/Products/index";
import AddProduct from "./components/Products/AddNewProduct/index";
import InventoryLayout from "./components/Inventory/index";
import Types from "./components/Inventory/Types/index";
import Warehouses from "./components/Inventory/Warehouses/index";
import StockProducts from "./components/Inventory/StockProducts/index";
import AddEditCrop from "./components/Inventory/StockProducts/AddEditCrop";
import Usage from "./components/Inventory/Usage/index";
import AddMovement from "./components/Inventory/Usage/AddMovement";
import WarehouseProducts from "./components/Inventory/Warehouses/WarehouseProducts/index";
import AssetsLayout from "./components/Assets/index";
import AssetsList from "./components/Assets/AssetsList";
import AssetPermissions from "./components/Assets/AssetPermissions/index";
import AssetRequests from "./components/Assets/AssetRequests/index";
import AssetExpenses from "./components/Assets/AssetExpenses/index";
import BusinessEntitiesList from "./components/BusinessEntities/BusinessEntitiesList";
import BusinessEntityForm from "./components/BusinessEntities/BusinessEntityForm";
import BusinessEntityProfile from "./components/BusinessEntities/BusinessEntityProfile";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/Header";
import { ProSidebarProvider } from "react-pro-sidebar";
import { useTranslation } from "react-i18next";
import { UserContext } from "./UserContext";
import { Toaster } from "react-hot-toast";
import { NotificationProvider } from "./contexts/NotificationContext";
import NotificationCenter from "./components/Notifications/NotificationCenter";

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { i18n } = useTranslation();
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const { showSubscriptionPlans, userData } = useContext(UserContext);

  useEffect(() => {
    const currentLang = i18n.language;
    document.body.style.direction = currentLang === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  return (
    <HashRouter>
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          // --- 1. Default Toast Style (Light Theme) ---
          // This style is designed to match your `.card` components.
          style: {
            background: "#ffffff",
            color: "#333",
            borderRadius: "12px", // Matches your .card-style border-radius
            border: "1px solid #dee2e6", // Subtle border like your cards
            padding: "12px 16px",
            fontSize: "0.95rem",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", // Matches --shadow-md
          },
          duration: 4000,

          // --- 2. Specific Toast Variants ---
          // Using the color palette from your design language.
          success: {
            duration: 3000,
            style: {
              background: "#28a745", // --color-success
              color: "#ffffff",
            },
            iconTheme: {
              primary: "#ffffff",
              secondary: "#28a745",
            },
          },

          error: {
            duration: 5000,
            style: {
              background: "#dc3545", // --color-danger
              color: "#ffffff",
            },
            iconTheme: {
              primary: "#ffffff",
              secondary: "#dc3545",
            },
          },

          // A new loading variant using your primary brand color.
          loading: {
            duration: 3000,
            style: {
              background: "#2c2a6c", // --color-primary
              color: "#ffffff",
            },
          },
        }}
      />
      {showSubscriptionPlans && <SubscriptionGate />}
      {isLoggedIn ? (
        <NotificationProvider>
          <div style={{ display: "flex" }}>
            <ProSidebarProvider>
              <Sidebar />
            </ProSidebarProvider>
            <div style={{ flex: 1 }}>
              <Header setIsLoggedIn={setIsLoggedIn} />
              <div style={{ padding: "20px" }}>
                <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/weather" element={<WeatherDetailsPage />} />
                <Route path="/fields" element={<FieldsList />} />
                <Route path="/fields/new" element={<FieldForm />} />
                <Route path="/fields/:fieldId" element={<FieldDetails />} />
                <Route path="/fields/:fieldId/edit" element={<FieldForm />} />
                <Route
                  path="/fields/:fieldId/transactions/new"
                  element={<TransactionForm />}
                />
                <Route
                  path="/fields/:fieldId/transactions/:transactionId/edit"
                  element={<TransactionForm />}
                />
                <Route path="/fields/new/map" element={<FieldMapEditor />} />
                <Route
                  path="/fields/:fieldId/edit/map"
                  element={<FieldMapEditor />}
                />
                <Route
                  path="/fields/:fieldId/plantations/new"
                  element={<CropCycleForm />}
                />
                <Route
                  path="/fields/:fieldId/plantations/:plantationId/edit"
                  element={<CropCycleForm />}
                />
                <Route
                  path="/fields/:fieldId/tasklists"
                  element={<TaskListSelector />}
                />
                <Route
                  path="/fields/:fieldId/tasklists/new"
                  element={<TaskListForm />}
                />
                <Route
                  path="/fields/:fieldId/tasklists/:taskListId/edit"
                  element={<TaskListForm />}
                />
                <Route
                  path="/fields/:fieldId/tasks/new"
                  element={<TaskForm />}
                />
                <Route
                  path="/fields/:fieldId/tasks/:taskId"
                  element={<TaskDetails />}
                />
                <Route
                  path="/fields/:fieldId/tasks/:taskId/edit"
                  element={<TaskForm />}
                />
                {/* Standalone Tasks Routes */}
                <Route path="/tasks" element={<Tasks />} />

                <Route path="/problems" element={<ProblemsList />} />
                <Route path="/problems/new" element={<ProblemForm />} />
                <Route
                  path="/problems/:problemId"
                  element={<ProblemDetails />}
                />
                <Route
                  path="/problems/:problemId/edit"
                  element={<ProblemForm />}
                />

                <Route path="/profile" element={<CompanyProfile />} />
                <Route path="/settings" element={<Settings />} />
                {/* Export feature disabled - hidden from all users */}
                {/* <Route path="/export" element={userData?.Role?.id === 1 ? <DataExport /> : <Navigate to="/dashboard" />} /> */}
                <Route path="/notifications" element={<NotificationCenter />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/transactions/new" element={<TransactionForm />} />
                <Route path="/transactions/:transactionId/edit" element={<TransactionForm />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/:invoiceId" element={<InvoiceDetails />} />
                <Route path="/employees" element={<EmployeesList />} />
                <Route path="/salaries-summary" element={<SalariesSummary />} />
                <Route
                  path="/employees/:employeeId"
                  element={<EmployeeProfile />}
                />
                <Route
                  path="/employees/:employeeId/transactions/new"
                  element={<AddTransactionPage />}
                />
                <Route
                  path="/employees/:employeeId/transactions/:transactionId/edit"
                  element={<AddTransactionPage />}
                />
                <Route path="/employees/new" element={<EmployeeForm />} />
                <Route path="/laborers/new" element={<EmployeeForm />} />
                <Route
                  path="/employees/:employeeId/edit"
                  element={<EmployeeForm />}
                />
                <Route path="/select-fields" element={<FieldSelector />} />
                <Route path="/employees/set-role" element={<SetRole />} />
                <Route
                  path="/select-workers"
                  element={<WorkerSelectorPage />}
                />
                <Route path="/business-entities" element={<BusinessEntitiesList />} />
                <Route path="/business-entities/new" element={<BusinessEntityForm />} />
                <Route path="/business-entities/edit/:entityId" element={<BusinessEntityForm />} />
                <Route path="/business-entities/profile/:entityId" element={<BusinessEntityProfile />} />
                <Route path="/entities/:entityId/transactions/new" element={<TransactionForm />} />
                <Route path="/entities/:entityId/transactions/:transactionId/edit" element={<TransactionForm />} />
                <Route path="/inventory/*" element={<InventoryLayout />}>
                  <Route path="warehouses" element={<Warehouses />} />
                  <Route path="types" element={<Types />} />
                  <Route path="stock" element={<StockProducts />} />{" "}
                  {/* Add Stock Products route */}
                  <Route path="stock-products/add-edit-crop" element={<AddEditCrop />} />
                  <Route path="usage" element={<Usage />} />
                  <Route path="usage/add" element={<AddMovement />} />
                  {/* Add other routes here */}
                </Route>
                <Route
                  path="/inventory/warehouses/:warehouseId/products"
                  element={<WarehouseProducts />}
                />
                <Route
                  path="/inventory/product/:productId/transactions"
                  element={<ProductTransactions />}
                />
                <Route
                  path="/inventory/product/:productId/transaction/new"
                  element={<AddProductMovementTransaction />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/inventory/warehouses" />}
                />

                {/* Assets Management Routes */}
                <Route path="/assets/*" element={<AssetsLayout />}>
                  <Route index element={<Navigate to="/assets/list" replace />} />
                  <Route path="list" element={<AssetsList />} />
                  <Route path="permissions" element={<AssetPermissions />} />
                  <Route path="requests" element={<AssetRequests />} />
                  <Route path="expenses" element={<AssetExpenses />} />
                </Route>

                <Route path="/products" element={<ProductsApp />} />
                <Route path="/add-product" element={<AddProduct />} />
                <Route
                  path="/edit-product/:id"
                  element={<AddProduct setShouldRefetch={setShouldRefetch} />}
                />
                {/* Fallback route for undefined paths */}
                <Route path="*" element={<Navigate to="/products" />} />
              </Routes>
            </div>
          </div>
        </div>
      </NotificationProvider>
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route
            path="/login"
            element={<Login setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route path="/signup" element={<SignUp />} />
          <Route
            path="/verify-phone"
            element={<PhoneVerification setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Fallback route for undefined paths */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
    </HashRouter>
  );
};

export default App;
